import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import PathCard from '../components/paths/PathCard'
import { Loader2 } from 'lucide-react'

export default function DisasterPathsScreen() {
  // Fetch all disaster paths
  const { data: paths, isLoading } = useQuery({
    queryKey: ['disaster-paths'],
    queryFn: async () => {
      const response = await fetch('/api/v1/paths')
      if (!response.ok) throw new Error('Failed to fetch paths')
      return response.json()
    }
  })

  // Fetch user's progress on paths
  const { data: userProgress } = useQuery({
    queryKey: ['user-path-progress'],
    queryFn: async () => {
      const response = await fetch('/api/v1/paths/progress')
      if (!response.ok) throw new Error('Failed to fetch progress')
      return response.json()
    }
  })

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-5xl font-bold mb-4">Disaster Survival Paths</h1>
            <p className="text-xl text-green-100 max-w-3xl mx-auto">
              Immersive, story-driven scenarios that guide you through realistic disaster situations from start to finish.
              Make critical decisions, manage resources, and learn survival skills that could save your life.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Paths Grid */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Available Paths */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Available Paths</h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {paths?.available?.map((path, index) => {
              const progress = userProgress?.find(p => p.path_id === path.id)

              return (
                <motion.div
                  key={path.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                >
                  <PathCard
                    slug={path.slug}
                    title={path.title}
                    description={path.description}
                    icon={path.icon}
                    difficulty={path.difficulty}
                    estimatedDuration={path.estimated_duration}
                    totalChapters={path.total_chapters}
                    completedChapters={progress?.completed_chapters ? JSON.parse(progress.completed_chapters).length : 0}
                    xpReward={path.xp_reward}
                    locked={false}
                    coverImage={path.cover_image_url}
                  />
                </motion.div>
              )
            })}
          </div>
        </motion.div>

        {/* Coming Soon Paths */}
        {paths?.coming_soon && paths.coming_soon.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Coming Soon</h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {paths.coming_soon.map((path, index) => (
                <motion.div
                  key={path.id || index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index }}
                >
                  <PathCard
                    slug={path.slug}
                    title={path.title}
                    description={path.description}
                    icon={path.icon}
                    difficulty={path.difficulty}
                    estimatedDuration={path.estimated_duration}
                    totalChapters={path.total_chapters}
                    xpReward={path.xp_reward}
                    locked={true}
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Stats Section */}
        {userProgress && userProgress.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-16 bg-white rounded-2xl border-2 border-gray-200 p-8"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Progress</h2>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-green-600 mb-2">
                  {userProgress.filter(p => p.completed_at).length}
                </div>
                <div className="text-gray-600">Paths Completed</div>
              </div>

              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">
                  {userProgress.reduce((sum, p) => sum + (p.total_xp_earned || 0), 0)}
                </div>
                <div className="text-gray-600">Total XP Earned</div>
              </div>

              <div className="text-center">
                <div className="text-4xl font-bold text-purple-600 mb-2">
                  {userProgress.reduce((sum, p) => {
                    const completed = p.completed_chapters ? JSON.parse(p.completed_chapters).length : 0
                    return sum + completed
                  }, 0)}
                </div>
                <div className="text-gray-600">Chapters Completed</div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  )
}
